import { useEffect, useState } from "react";
import type { IncidentDTO, IncidentImpact, IncidentStatus, StatusResponse } from "@currencycore/shared";
import {
  createIncident,
  deleteIncident,
  getStatusSummary,
  postIncidentUpdate,
  ApiRequestError,
} from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";

const IMPACTS: IncidentImpact[] = ["minor", "major", "critical"];
const STATUSES: IncidentStatus[] = ["investigating", "identified", "monitoring", "resolved"];

const STATUS_LABEL: Record<string, string> = {
  operational: "All systems operational",
  degraded: "Degraded performance",
  outage: "Service outage",
};
const STATUS_COLOR: Record<string, string> = {
  operational: "var(--ok, #16a34a)",
  degraded: "var(--warn, #d97706)",
  outage: "var(--danger, #dc2626)",
};
const IMPACT_COLOR: Record<IncidentImpact, string> = {
  minor: "var(--warn, #d97706)",
  major: "var(--danger, #dc2626)",
  critical: "var(--danger, #dc2626)",
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function errMsg(e: unknown): string {
  return e instanceof ApiRequestError ? e.body.error.message : "Something went wrong. Try again.";
}

export function IncidentsPage() {
  const [summary, setSummary] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // New-incident form.
  const [title, setTitle] = useState("");
  const [impact, setImpact] = useState<IncidentImpact>("minor");
  const [status, setStatus] = useState<IncidentStatus>("investigating");
  const [message, setMessage] = useState("");

  async function reload() {
    try {
      setSummary(await getStatusSummary());
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast("Title and an initial message are required.");
      return;
    }
    setBusy(true);
    try {
      await createIncident({ title: title.trim(), impact, status, body: message.trim() });
      setTitle("");
      setMessage("");
      setImpact("minor");
      setStatus("investigating");
      toast("Incident created.", true);
      await reload();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this incident and its timeline?")) return;
    try {
      await deleteIncident(id);
      toast("Incident deleted.", true);
      await reload();
    } catch (e) {
      toast(errMsg(e));
    }
  }

  const incidents = summary?.incidents ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Status &amp; incidents</h1>
          <p className="muted">Current health, 90-day uptime, and the incident timeline shown on the public status page.</p>
        </div>
      </div>

      {/* Current status + uptime readout */}
      <div className="card" style={{ padding: "1.1rem 1.3rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 999,
            background: summary ? STATUS_COLOR[summary.status] : "var(--text-subtle, #888)",
            boxShadow: summary ? `0 0 0 4px color-mix(in oklch, ${STATUS_COLOR[summary.status]} 18%, transparent)` : "none",
            flex: "0 0 auto",
          }}
        />
        <strong style={{ fontSize: 15 }}>{loading ? "Loading…" : STATUS_LABEL[summary!.status]}</strong>
        {summary && (
          <span className="muted" style={{ marginLeft: "auto", fontSize: 13 }}>
            90-day uptime <strong style={{ color: "var(--text)" }}>{summary.uptime.pct90}%</strong>
          </span>
        )}
      </div>

      {/* New incident */}
      <form className="card" onSubmit={submitNew} style={{ padding: "1.2rem 1.3rem", marginTop: "1rem", display: "grid", gap: "0.8rem" }}>
        <strong style={{ fontSize: 14 }}>Post a new incident</strong>
        <input
          type="text"
          placeholder="Incident title (e.g. Elevated conversion latency)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={inputStyle}
        />
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <label style={fieldStyle}>
            <span className="muted" style={labelStyle}>Impact</span>
            <select value={impact} onChange={(e) => setImpact(e.target.value as IncidentImpact)} style={inputStyle}>
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label style={fieldStyle}>
            <span className="muted" style={labelStyle}>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus)} style={inputStyle}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <textarea
          placeholder="Initial update message shown on the status page…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Posting…" : "Create incident"}
          </button>
        </div>
      </form>

      {/* Incident list */}
      <div style={{ marginTop: "1.4rem", display: "grid", gap: "1rem" }}>
        {loading ? (
          <div className="card" style={{ padding: "1.2rem", color: "var(--text-muted)" }}>Loading incidents…</div>
        ) : incidents.length === 0 ? (
          <div className="card" style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)" }}>
            No incidents in the last 90 days.
          </div>
        ) : (
          incidents.map((inc) => <IncidentCard key={inc.id} inc={inc} onChanged={reload} onDelete={remove} />)
        )}
      </div>
    </div>
  );
}

function IncidentCard({
  inc,
  onChanged,
  onDelete,
}: {
  inc: IncidentDTO;
  onChanged: () => Promise<void>;
  onDelete: (id: string) => void;
}) {
  const [upStatus, setUpStatus] = useState<IncidentStatus>(inc.status === "resolved" ? "monitoring" : inc.status);
  const [upBody, setUpBody] = useState("");
  const [busy, setBusy] = useState(false);
  const resolved = inc.status === "resolved";

  async function post(e: React.FormEvent) {
    e.preventDefault();
    if (!upBody.trim()) {
      toast("An update message is required.");
      return;
    }
    setBusy(true);
    try {
      await postIncidentUpdate(inc.id, { status: upStatus, body: upBody.trim() });
      setUpBody("");
      toast("Update posted.", true);
      await onChanged();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ padding: "1.1rem 1.3rem", opacity: resolved ? 0.85 : 1 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.7rem", flexWrap: "wrap" }}>
        <strong style={{ fontSize: 15 }}>{inc.title}</strong>
        <span style={badge(IMPACT_COLOR[inc.impact])}>{inc.impact}</span>
        <span style={badge(resolved ? "var(--ok, #16a34a)" : "var(--warn, #d97706)")}>{inc.status}</span>
        {inc.auto && <span style={badge("var(--text-subtle, #888)")}>auto</span>}
        <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
          {fmtDate(inc.startedAt)}{inc.resolvedAt ? ` → resolved ${fmtDate(inc.resolvedAt)}` : ""}
        </span>
        <button type="button" className="btn" style={{ fontSize: 12 }} onClick={() => onDelete(inc.id)}>
          Delete
        </button>
      </div>

      {/* Timeline */}
      <ul style={{ listStyle: "none", margin: "0.9rem 0 0", padding: 0, display: "grid", gap: "0.55rem" }}>
        {inc.updates.map((u, i) => (
          <li key={i} style={{ display: "flex", gap: "0.6rem", fontSize: 13 }}>
            <span className="muted" style={{ minWidth: 96, fontVariantNumeric: "tabular-nums" }}>{fmtDate(u.createdAt)}</span>
            <span style={{ fontWeight: 600, minWidth: 96, textTransform: "capitalize" }}>{u.status}</span>
            <span style={{ color: "var(--text-muted)" }}>{u.body}</span>
          </li>
        ))}
      </ul>

      {/* Post update */}
      <form onSubmit={post} style={{ display: "flex", gap: "0.5rem", marginTop: "0.9rem", flexWrap: "wrap", alignItems: "center" }}>
        <select value={upStatus} onChange={(e) => setUpStatus(e.target.value as IncidentStatus)} style={{ ...inputStyle, width: "auto" }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="text"
          placeholder="Post an update…"
          value={upBody}
          onChange={(e) => setUpBody(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        <button type="submit" className="btn btn-primary" disabled={busy} style={{ fontSize: 13 }}>
          {busy ? "Posting…" : resolved ? "Re-open / update" : "Post update"}
        </button>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--surface-2, #1a1a1a)",
  border: "1px solid var(--border, #333)",
  borderRadius: 8,
  color: "var(--text, #eee)",
  padding: "0.5rem 0.7rem",
  fontSize: 14,
  width: "100%",
};
const fieldStyle: React.CSSProperties = { display: "grid", gap: "0.25rem", flex: "1 1 140px" };
const labelStyle: React.CSSProperties = { fontSize: 12 };
const badge = (color: string): React.CSSProperties => ({
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color,
  border: `1px solid color-mix(in oklch, ${color} 45%, transparent)`,
  background: `color-mix(in oklch, ${color} 12%, transparent)`,
  padding: "0.1rem 0.45rem",
  borderRadius: 999,
});
